/**
 * inject-reviews-wedding-hanger.js
 *
 * Fetches real reviews from the Ximple API and injects them as
 * "front-loaded" reviews into the Wise Reviews widget on the
 * Personalized Wedding Dress Hanger product page.
 *
 * - Initial load: first 20 reviews (newest first).
 * - "Show More" clicks inject next batch from ximpleapp until exhausted.
 * - After all ximpleapp reviews are shown, Wise Reviews "Show More" works natively.
 */

(function () {
  const API_URL = 'https://ximpleapp.com/code/personalizan/reviews/';
  const INITIAL_BATCH = 20;

  /* ─── Shared state ───────────────────────────────────────────────────────── */
  var allReviews = [];       // all normalised ximpleapp reviews (newest-first)
  var injectedCount = 0;     // how many have been injected so far
  var observerStarted = false;

  /* ─── SVG star helpers ───────────────────────────────────────────────────── */
  const STAR_PATH = 'M 15 0.710938 L 18.738281 9.109375 C 18.882812 9.433594 19.105469 9.710938 19.394531 9.917969 C 19.679688 10.125 20.015625 10.253906 20.367188 10.289062 L 29.511719 11.25 L 22.675781 17.394531 C 22.414062 17.628906 22.21875 17.933594 22.109375 18.265625 C 22 18.601562 21.980469 18.960938 22.054688 19.308594 L 23.96875 28.296875 L 16.007812 23.699219 C 15.699219 23.519531 15.351562 23.429688 15 23.429688 C 14.648438 23.429688 14.300781 23.519531 13.992188 23.699219 L 6.03125 28.296875 L 7.945312 19.308594 C 8.019531 18.960938 8 18.601562 7.890625 18.265625 C 7.78125 17.933594 7.585938 17.628906 7.324219 17.394531 L 0.488281 11.25 L 9.632812 10.289062 C 9.984375 10.253906 10.320312 10.125 10.605469 9.917969 C 10.894531 9.710938 11.117188 9.433594 11.261719 9.109375 Z M 15 0.710938';

  function buildStarSVG(color) {
    return `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 30 30.000001" style="height:18px;width:18px;max-width:none;">
      <defs><clipPath><path d="M 0.484375 0.710938 L 29.515625 0.710938 L 29.515625 28.292969 L 0.484375 28.292969 Z" clip-rule="nonzero"></path></clipPath></defs>
      <g><path fill="${color}" d="${STAR_PATH}" fill-opacity="1" fill-rule="evenodd"></path></g>
    </svg>`;
  }

  function buildStarRating(rating) {
    let stars = '';
    for (let i = 1; i <= 5; i++) {
      const filled = i <= rating;
      stars += `<span style="cursor:inherit;display:inline-block;position:relative;">
        <span style="visibility:hidden;">${buildStarSVG('#cccccc')}</span>
        <span style="display:inline-block;position:absolute;overflow:hidden;top:0;left:0;width:${filled ? '100' : '0'}%;">${buildStarSVG('#f4c01e')}</span>
      </span>`;
    }
    return `<span class="wise-inline-flex wise-items-center" style="display:inline-block;direction:ltr;">${stars}</span>`;
  }

  /* ─── Normalise an API review ────────────────────────────────────────────── */
  function normaliseReview(raw) {
    return {
      id: String(raw.id),
      reviewer_name: (function (name) {
        var n = name || '';
        return n.length > 20 ? n.substring(0, 20) + '…' : n;
      })(raw.reviewer_name),
      reviewer_name_full: raw.reviewer_name,
      reviewer_initials: (function (name) {
        var parts = name.trim().split(/\s+/);
        if (parts.length >= 2) {
          return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
        }
        return (name.substring(0, 2) || name).toUpperCase();
      })(raw.reviewer_name),
      rating: raw.rating,
      body: raw.body,
      date: raw.date,
      verified: Boolean(raw.verified),
      helpful_count: raw.helpful_count || 0,
      media_url: (raw.media_urls && raw.media_urls.length > 0) ? raw.media_urls[0] : null,
    };
  }

  /* ─── Extract Wise Reviews summary from DOM (before injection clears it) ─── */
  function extractWiseSummary() {
    var wiseSection = document.querySelector('.wise-product-reviews');
    if (!wiseSection) return null;

    // Find the rating value (e.g., "4.9")
    var ratingEl = wiseSection.querySelector('p.wise-text-3xl');
    var avgText = ratingEl ? ratingEl.textContent.trim() : '';
    var avg = avgText ? parseFloat(avgText) : 0;

    // Find the review count (e.g., "97 reviews")
    var countEl = wiseSection.querySelector('p.wise-text-sm.wise-font-medium');
    var countText = countEl ? countEl.textContent.trim() : '';
    var count = parseInt(countText.replace(/[^0-9]/g, ''), 10) || 0;

    // Find breakdown percentages (5-star, 4-star, 3-star, 2-star, 1-star)
    var pctEls = wiseSection.querySelectorAll('span.wise-text-sm.wise-font-medium.wise-whitespace-nowrap');
    var pcts = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    for (var i = 0; i < Math.min(pctEls.length, 5); i++) {
      var pctText = pctEls[i].textContent.trim();
      var pctVal = parseInt(pctText.replace('%', ''), 10) || 0;
      pcts[5 - i] = pctVal;
    }

    return { avg: avg, count: count, pcts: pcts };
  }

  /* ─── Compute combined rating summary (XimpleApp + Wise) ─────────────────── */
  function computeCombinedSummary(ximpleReviews, wiseSummary) {
    if (!ximpleReviews || ximpleReviews.length === 0) return null;

    // XimpleApp distribution
    var xDist = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    var xSum = 0;
    for (var i = 0; i < ximpleReviews.length; i++) {
      var r = ximpleReviews[i].rating;
      xSum += r;
      if (xDist.hasOwnProperty(r)) xDist[r]++;
    }

    // Wise Reviews distribution (derived from avg, count, pcts)
    var wCount = wiseSummary ? wiseSummary.count : 0;
    var wSum = wiseSummary ? wiseSummary.avg * wCount : 0;
    var wDist = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    if (wiseSummary && wiseSummary.pcts) {
      for (var star in wiseSummary.pcts) {
        wDist[star] = Math.round((wiseSummary.pcts[star] / 100) * wCount);
      }
    }

    // Combined
    var totalCount = ximpleReviews.length + wCount;
    if (totalCount === 0) return null;
    var totalSum = xSum + wSum;
    var combinedDist = {};
    for (var s in xDist) {
      combinedDist[s] = xDist[s] + (wDist[s] || 0);
    }

    // Round avg to 1 decimal, always show 1 decimal place
    var avgRounded = (Math.round((totalSum / totalCount) * 10) / 10).toFixed(1);

    // Compute percentages
    var pcts = {};
    for (var st in combinedDist) {
      pcts[st] = Math.round((combinedDist[st] / totalCount) * 100);
    }
    // Adjust for rounding to sum to 100
    var totalPct = 0;
    for (var ss in pcts) totalPct += pcts[ss];
    var diff = 100 - totalPct;
    if (diff !== 0) {
      var maxStar = '5', maxC = 0;
      for (var sss in combinedDist) {
        if (combinedDist[sss] > maxC) { maxC = combinedDist[sss]; maxStar = sss; }
      }
      pcts[maxStar] += diff;
    }

    return { avg: avgRounded, count: totalCount, distribution: combinedDist, pcts: pcts };
  }

  /* ─── Build Material Symbols star HTML for a given rating ────────────────── */
  function buildMaterialStars(rating) {
    if (!rating) return '';
    var decimal = 0;
    var mod = rating % 1;
    if (mod >= 0.3 && mod <= 0.7) decimal = 0.5;
    else if (mod > 0.7) decimal = 1;
    var val = Math.floor(rating);
    var max = 5;
    var valPlus1 = val + 1;
    var html = '';
    for (var i = 1; i <= max; i++) {
      if (i <= val) {
        html += '<span class="material-symbols-outlined text-primary material-filled">star</span>';
      } else if (i === valPlus1 && decimal > 0) {
        html += '<span class="material-symbols-outlined text-primary material-filled">star_half</span>';
      } else {
        html += '<span class="material-symbols-outlined material-outline text-gray-300">star</span>';
      }
    }
    return html;
  }

  /* ─── Build rating bars HTML ─────────────────────────────────────────────── */
  function buildRatingBarsHTML(pcts, suffix) {
    suffix = suffix || '';
    var html = '';
    for (var star = 5; star >= 1; star--) {
      var pct = pcts[star] || 0;
      html += '<p class="text-sm">' + star + '</p>';
      html += '<div class="flex h-2 flex-1 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">' +
        '<div id="reviews-bar-' + star + suffix + '" class="rounded-full bg-primary" style="width: ' + pct + '%;"></div></div>';
      html += '<p id="reviews-bar-' + star + '-pct' + suffix + '" class="text-xs text-right opacity-70">' + pct + '%</p>';
    }
    return html;
  }

  /* ─── Update rating summary DOM elements ─────────────────────────────────── */
  function updateRatingSummary(summary) {
    if (!summary) return;
    console.log('[inject-reviews] Rating summary: ' + summary.avg + ' avg, ' + summary.count + ' reviews');

    /* ── Inline rating (block-based layout) ──
       The *-count id exists in BOTH the metafield-present and metafield-absent
       branches of the rating block, so update whichever elements are present.
       The #ximple-rating-inline wrapper only exists in the metafield-absent
       branch (hidden until populated), so only clear its display if it exists. */
    var starsEl = document.getElementById('ximple-rating-inline-stars');
    var valueEl = document.getElementById('ximple-rating-inline-value');
    var countEl = document.getElementById('ximple-rating-inline-count');
    if (starsEl) starsEl.innerHTML = buildMaterialStars(summary.avg);
    if (valueEl) valueEl.textContent = summary.avg;
    if (countEl) countEl.textContent = summary.count + ' reviews';
    var inlineEl = document.getElementById('ximple-rating-inline');
    if (inlineEl) inlineEl.style.display = '';

    /* ── Reviews summary section (block-based layout) ── */
    var summaryEl = document.getElementById('reviews-summary');
    if (summaryEl) {
      var rvEl = document.getElementById('reviews-rating-value');
      var rsEl = document.getElementById('reviews-stars');
      var rcEl = document.getElementById('reviews-count');
      var rbEl = document.getElementById('reviews-bars');
      if (rvEl) rvEl.textContent = summary.avg;
      if (rsEl) rsEl.innerHTML = buildMaterialStars(summary.avg);
      if (rcEl) rcEl.innerHTML = '<a href="#reviews" class="text-gray-500 dark:text-gray-400 underline decoration-gray-300 hover:text-primary transition-colors">' + summary.count + ' total reviews</a>';
      if (rbEl) rbEl.innerHTML = buildRatingBarsHTML(summary.pcts, '');
      summaryEl.style.display = '';
    }

    /* ── Reviews summary section (fallback layout) ── */
    var summaryFbEl = document.getElementById('reviews-summary-fb');
    if (summaryFbEl) {
      var rvFbEl = document.getElementById('reviews-rating-value-fb');
      var rsFbEl = document.getElementById('reviews-stars-fb');
      var rcFbEl = document.getElementById('reviews-count-fb');
      var rbFbEl = document.getElementById('reviews-bars-fb');
      if (rvFbEl) rvFbEl.textContent = summary.avg;
      if (rsFbEl) rsFbEl.innerHTML = buildMaterialStars(summary.avg);
      if (rcFbEl) rcFbEl.innerHTML = '<a href="#reviews" class="text-gray-500 dark:text-gray-400 underline decoration-gray-300 hover:text-primary transition-colors">' + summary.count + ' total reviews</a>';
      if (rbFbEl) rbFbEl.innerHTML = buildRatingBarsHTML(summary.pcts, '-fb');
      summaryFbEl.style.display = '';
    }
  }

 /* ─── Review photo lightbox ───────────────────────────────────────────────── */
  var reviewLightboxImages = [];
  var reviewLightboxIndex = 0;
  var reviewLightboxCreated = false;

  function fullSizeReviewPhoto(url) {
    try {
      var imageUrl = new URL(url);
      if (imageUrl.hostname === 'i.etsystatic.com') {
        imageUrl.pathname = imageUrl.pathname.replace(/\/(iap|il)_\d+x(?:\d+|N)\./, '/$1_fullxfull.');
      }
      return imageUrl.href;
    } catch (_) {
      return url;
    }
  }

  function createReviewLightbox() {
    if (reviewLightboxCreated) return;
    reviewLightboxCreated = true;

    var lb = document.createElement('div');
    lb.id = 'review-photo-lightbox';
    lb.className = 'fixed inset-0 z-[200] bg-black/90 hidden items-center justify-center';
    lb.setAttribute('role', 'dialog');
    lb.setAttribute('aria-modal', 'true');
    lb.setAttribute('aria-label', 'Review photo');
    lb.innerHTML =
      '<button type="button" class="review-lb-close absolute top-4 right-4 z-10 flex items-center justify-center w-10 h-10 rounded-full bg-white/20 text-white hover:bg-white/40 transition-colors cursor-pointer" aria-label="Close">' +
        '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>' +
      '</button>' +
      '<img class="review-lb-img" src="" alt="Customer review photo">' +
      '<div class="review-lb-navigation" role="group" aria-label="Review photo navigation">' +
      '<button type="button" class="review-lb-prev flex items-center justify-center rounded-full bg-white/20 text-white hover:bg-white/40 transition-colors cursor-pointer" aria-label="Previous">' +
        '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/></svg>' +
      '</button>' +
      '<button type="button" class="review-lb-next flex items-center justify-center rounded-full bg-white/20 text-white hover:bg-white/40 transition-colors cursor-pointer" aria-label="Next">' +
        '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/></svg>' +
      '</button></div>';
    document.body.appendChild(lb);

    // Event listeners
    lb.querySelector('.review-lb-close').addEventListener('click', closeReviewLightbox);
    lb.querySelector('.review-lb-prev').addEventListener('click', reviewLightboxPrev);
    lb.querySelector('.review-lb-next').addEventListener('click', reviewLightboxNext);
    lb.addEventListener('click', function (e) {
      if (e.target === lb) closeReviewLightbox();
    });
  }

  function updateReviewLightboxImage() {
    var img = document.querySelector('#review-photo-lightbox .review-lb-img');
    if (img && reviewLightboxImages.length > 0) {
      var original = reviewLightboxImages[reviewLightboxIndex];
      img.onerror = function () {
        img.onerror = null;
        img.src = original;
      };
      img.src = fullSizeReviewPhoto(original);
    }
  }

  function openReviewLightbox(url) {
    // Collect all review photo URLs
    reviewLightboxImages = [];
    var photos = document.querySelectorAll('.wise-product-reviews [data-review-photo-url]');
    for (var i = 0; i < photos.length; i++) {
      reviewLightboxImages.push(photos[i].getAttribute('data-review-photo-url'));
    }
    if (reviewLightboxImages.length === 0) return;

    // Find current image index
    reviewLightboxIndex = 0;
    for (var j = 0; j < reviewLightboxImages.length; j++) {
      if (reviewLightboxImages[j] === url) { reviewLightboxIndex = j; break; }
    }

    createReviewLightbox();
    updateReviewLightboxImage();
    var lb = document.getElementById('review-photo-lightbox');
    lb.classList.remove('hidden');
    lb.classList.add('flex');
    document.body.style.overflow = 'hidden';

    // Show/hide nav buttons
    var prevBtn = lb.querySelector('.review-lb-prev');
    var nextBtn = lb.querySelector('.review-lb-next');
    if (reviewLightboxImages.length <= 1) {
      prevBtn.style.display = 'none';
      nextBtn.style.display = 'none';
    } else {
      prevBtn.style.display = '';
      nextBtn.style.display = '';
    }
  }

  function closeReviewLightbox() {
    var lb = document.getElementById('review-photo-lightbox');
    if (!lb) return;
    lb.classList.add('hidden');
    lb.classList.remove('flex');
    document.body.style.overflow = '';
  }

  function reviewLightboxPrev() {
    if (reviewLightboxImages.length <= 1) return;
    reviewLightboxIndex = (reviewLightboxIndex - 1 + reviewLightboxImages.length) % reviewLightboxImages.length;
    updateReviewLightboxImage();
  }

  function reviewLightboxNext() {
    if (reviewLightboxImages.length <= 1) return;
    reviewLightboxIndex = (reviewLightboxIndex + 1) % reviewLightboxImages.length;
    updateReviewLightboxImage();
  }

  /* ─── Card builder ───────────────────────────────────────────────────────── */
  function buildReviewCard(review) {
    const hasPhoto = Boolean(review.media_url);
    const verifiedBadge = review.verified
      ? `<img src="https://wise-reviews.assets.codexapps.co/assets/verified.png" alt="Verified" loading="lazy" class="wise-w-5 wise-h-5 -wise-ml-[15px] wise-mt-[25px]">`
      : '';

    const photoSection = hasPhoto
      ? `<div>
           <img loading="lazy" alt="Review media" src="${review.media_url}"
             data-review-photo-url="${review.media_url}"
             class="wise-object-cover wise-object-center wise-aspect-square wise-w-full wise-rounded-t-lg wise-transition wise-duration-300 wise-block review-photo-clickable"
             style="cursor:pointer;">
         </div>
         <div class="wise-flex wise-justify-center">
           <div class="wise-shadow-md wise-bg-white dark:wise-bg-gray-800 wise-py-1 wise-px-3 wise-rounded-full wise-absolute -wise-mt-[13px] wise-leading-none">
             ${buildStarRating(review.rating)}
           </div>
         </div>`
      : '';

    const inlineStarSection = !hasPhoto
      ? `<div class="wise-flex wise-items-center wise-mb-2">
          ${buildStarRating(review.rating)}
        </div>`
      : '';

    return `<div class="dark:wise-bg-gray-800 wise-bg-white wise-opacity-90 wise-border wise-rounded-lg wise-w-full wise-mb-4" data-mock-review="${review.id}">
      ${photoSection}
      <div class="wise-review-content-container wise-p-3 md:wise-p-4">
        <div class="wise-block">
          ${inlineStarSection}
          <div class="wise-mb-4 wise-flex wise-items-center">
            <div class="wise-flex wise-items-center">
              <div class="wise-w-10 wise-h-10 wise-rounded-full wise-aspect-square wise-font-semibold wise-justify-center wise-items-center wise-flex wise-text-black wise-bg-slate-200">
                ${review.reviewer_initials}
              </div>
              ${verifiedBadge}
            </div>
            <div class="wise-ml-2">
              <div class="wise-font-medium wise-truncate wise-text-black dark:wise-text-white" title="${review.reviewer_name_full}">
                ${review.reviewer_name}
              </div>
            </div>
          </div>
          <div class="wise-flex wise-flex-wrap">
            <p class="wise-mb-2 wise-text-black dark:wise-text-white wise-text-sm wise-break-words wise-max-w-full">
              ${review.body}
            </p>
          </div>
          <div>
            <div class="wise-flex wise-items-center wise-justify-between wise-mt-3 wise-space-x-3">
              <button type="button"
                class="wise-opacity-90 focus:wise-outline-none wise-transition wise-duration-300 wise-text-gray-900 dark:wise-text-gray-200 focus:wise-ring-gray-200 wise-font-medium wise-rounded-full wise-text-xs wise-py-1.5 wise-border wise-border-gray-300 hover:wise-bg-gray-100 focus:wise-ring-4 wise-px-2">
                Helpful${review.helpful_count > 0 ? ` · ${review.helpful_count}` : ''}
              </button>
              <p class="wise-text-xs wise-text-gray-500 dark:wise-text-gray-400">${review.date}</p>
            </div>
          </div>
        </div>
      </div>
    </div>`;
  }

  /* ─── Inject a batch of reviews into masonry columns ─────────────────────── */
  function injectBatch(batch) {
    if (!window.location.pathname.includes('personalized-wedding-dress-hanger')) return;
    if (document.querySelector('[data-mock-reviews-injected]')) return;

    var reviewsSection = document.querySelector('.wise-product-reviews');
    if (!reviewsSection) return;

    var columns = reviewsSection.querySelectorAll('.wr-id-review-column');
    if (!columns || columns.length === 0) return;

    var numCols = columns.length;

    // Clear existing Wise Reviews cards so only ximpleapp reviews are visible.
    // Wise Reviews fetches its own data from codexapps on page load, but we
    // want ximpleapp reviews to appear first. Remove cards that don't have
    // our data-mock-review attribute (i.e. native Wise Reviews cards).
    for (var c = 0; c < columns.length; c++) {
      var children = Array.prototype.slice.call(columns[c].children);
      for (var k = children.length - 1; k >= 0; k--) {
        if (!children[k].hasAttribute('data-mock-review')) {
          columns[c].removeChild(children[k]);
        }
      }
    }

    // Prepend in reverse order so newest (index 0) ends up on top.
    for (var i = batch.length - 1; i >= 0; i--) {
      var review = batch[i];
      var targetCol = columns[i % numCols];
      targetCol.insertAdjacentHTML('afterbegin', buildReviewCard(review));
    }

    injectedCount += batch.length;
    reviewsSection.setAttribute('data-mock-reviews-injected', 'true');
  }

  /* ─── Intercept "Show More" button ────────────────────────────────────────── */
  function setupShowMoreInterceptor() {
    if (observerStarted) return;
    observerStarted = true;

    var reviewsSection = document.querySelector('.wise-product-reviews');
    if (!reviewsSection) return;

    // Use event delegation: catch clicks on / inside the "Show More" button
    reviewsSection.addEventListener('click', function (e) {
      // Find the button that was clicked (or a parent of the click target)
      var target = e.target.closest('button');
      if (!target) return;

      var text = target.textContent.trim().toLowerCase();
      if (!text.includes('show more')) return;

      // Check if we still have ximpleapp reviews to show
      if (injectedCount >= allReviews.length) {
        // All ximpleapp reviews injected — let Wise Reviews handle it
        return;
      }

      // Block the Wise Reviews "Show More" click
      e.preventDefault();
      e.stopPropagation();

      // Hide the button to prevent double-clicks while we inject
      target.style.display = 'none';

      console.log('[inject-reviews] Intercepting Show More. Injecting batch ' +
        (injectedCount + 1) + '-' + Math.min(injectedCount + 20, allReviews.length));

      // Compute next batch (up to 20)
      var remaining = allReviews.slice(injectedCount);
      var batch = remaining.slice(0, 20);

      // Get current columns
      var columns = reviewsSection.querySelectorAll('.wr-id-review-column');
      if (!columns || columns.length === 0) return;
      var numCols = columns.length;

      // Append to BOTTOM of each column (append so they appear below existing)
      for (var i = 0; i < batch.length; i++) {
        var targetCol = columns[i % numCols];
        targetCol.insertAdjacentHTML('beforeend', buildReviewCard(batch[i]));
      }

      injectedCount += batch.length;

      // Re-show button after a delay so Wise Reviews can re-render
      setTimeout(function () {
        target.style.display = '';
      }, 1500);
    }, true); // capture phase to intercept before React
  }

  /* ─── Review photo click handler (event delegation) ──────────────────────── */
  var photoClickHandlerSetup = false;
  function setupPhotoClickHandler() {
    if (photoClickHandlerSetup) return;
    photoClickHandlerSetup = true;

    var reviewsSection = document.querySelector('.wise-product-reviews');
    if (!reviewsSection) return;

    // Click on review photos to open lightbox
    reviewsSection.addEventListener('click', function (e) {
      var photo = e.target.closest('.review-photo-clickable');
      if (!photo) return;

      var url = photo.getAttribute('data-review-photo-url');
      if (!url) return;

      e.preventDefault();
      e.stopPropagation();
      openReviewLightbox(url);
    });
  }

  // Keyboard navigation for review lightbox
  document.addEventListener('keydown', function (e) {
    var lb = document.getElementById('review-photo-lightbox');
    if (!lb || lb.classList.contains('hidden')) return;

    if (e.key === 'Escape') closeReviewLightbox();
    else if (e.key === 'ArrowLeft') reviewLightboxPrev();
    else if (e.key === 'ArrowRight') reviewLightboxNext();
  });

  /* ─── Fetch reviews from API then inject ─────────────────────────────────── */
  function fetchAndInject() {
    fetch(API_URL)
      .then(function (res) { return res.json(); })
      .then(function (json) {
        allReviews = (json.data || []).map(normaliseReview);
        // Sort newest-first
        allReviews.sort(function (a, b) {
          return Date.parse(b.date) - Date.parse(a.date);
        });
        console.log('[inject-reviews] Fetched ' + allReviews.length + ' reviews from ximpleapp.');

        // Inject first batch (waitAndInject also extracts Wise data & updates summary)
        var initial = allReviews.slice(0, INITIAL_BATCH);
        waitAndInject(initial);
      })
      .catch(function (err) {
        console.error('[inject-reviews] Failed to fetch reviews:', err);
      });
  }

  /* ─── Wait for Wise Reviews widget to render, then inject ────────────────── */
  function waitAndInject(reviews, attempts) {
    attempts = attempts || 0;
    if (attempts > 40) return; // give up after ~4 seconds

    var columns = document.querySelectorAll('.wise-product-reviews .wr-id-review-column');
    if (columns && columns.length > 0) {
      // Extract Wise Reviews summary BEFORE injection clears native Wise cards
      var wiseSummary = extractWiseSummary();
      if (wiseSummary) {
        console.log('[inject-reviews] Wise Reviews: ' + wiseSummary.avg + ' avg, ' + wiseSummary.count + ' reviews');
      }

      // Compute combined summary (XimpleApp + Wise) and update DOM
      var ratingSummary = computeCombinedSummary(allReviews, wiseSummary);
      updateRatingSummary(ratingSummary);

      // Anchor: move #reviews id to the Wise Reviews widget so
      // clicking "X reviews" link jumps here instead of the inline summary
      var wiseSection = document.querySelector('.wise-product-reviews');
      if (wiseSection) {
        wiseSection.id = 'reviews';
      }

      injectBatch(reviews);

      // Set up review photo click handler after initial injection
      setupPhotoClickHandler();

      // Set up "Show More" interceptor after initial injection
      if (injectedCount < allReviews.length) {
        setTimeout(setupShowMoreInterceptor, 1500);
      }
    } else {
      setTimeout(function () {
        waitAndInject(reviews, attempts + 1);
      }, 100);
    }
  }

  // Start fetching after DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { fetchAndInject(); });
  } else {
    fetchAndInject();
  }
})();
